// BUILD-046 correction cycle 1bb88b3e (stage B1, items N1 and N9): the counts shown with each view, LABELLED and never mixed.
//
// The Owner's review found the personal "Active 1" beside unrelated team totals. The rule here: the counts of the SELECTED view describe that view and
// nothing else, and the personal bell figures are a separate, always-personal pair with their own labels. There is no total anywhere in this module and no
// figure that adds a personal number to a team number.
//
//   My Work        : Active, Waiting, Blocked, Completed and Overdue of the Caller's OWN loaded tasks.
//   Group / Shared : how many tasks in the shared queue the Caller can take (the loaded list), nothing about anyone else's work.
//   Team Work      : the tasks of the Caller's oversight scope, labelled with that scope ("direct reports" or "company") and with the filter (open or
//                    completed). A list that is read page by page and not fully loaded says so ("50+", `partial`), never a total it does not have.
// A number that cannot be read is `null` with the text "Unavailable" (or "…" while loading), never 0.
//
// Owner-requested layout revision (Layout-5): the separate "Your bell" strip is gone (it repeated the tab counts), so this module no longer describes a
// personal pair. What replaces it is `describeTabCounts` below: the number beside each TAB, shown only where an accurate, clearly scoped figure already
// exists, and absent otherwise (never 0 for unknown, never a figure that could be partial or from another scope).
//
// Pure module. Type-only imports, and the one overdue rule (overdueRule.ts, pure). Erasable TypeScript only.

import type { HostView } from './hostViewsModel';
import type { CountsView } from './workCountsController';
import type { OversightFilter } from './oversightCompletedModel';
// Cycle A (2026-10-21): the Overdue figure is counted by the one overdue rule (a runtime import of a pure module).
import { isOverdueTask } from './overdueRule';
import { isOpenTask } from './mineScopeModel';
import type { OverdueTask } from './overdueRule';
import { followUpDueOn } from './attentionModel';

export interface CountFigure {
  key: string;
  label: string;
  /** The number, or null while loading / when it cannot be read. */
  value: number | null;
  /** What to show: the number (with `+` when more exist than are loaded), "…" while loading, "Unavailable" when it cannot be read. */
  text: string;
  /** True when more exist than are loaded: the figure is a lower bound. */
  partial: boolean;
}

export interface ViewCounts {
  view: HostView;
  /** Names what the figures count, e.g. "Your tasks", "Shared queue you can take", "Team Work (direct reports)". */
  heading: string;
  /** The figures of the selected view, and only of it. */
  figures: CountFigure[];
}

export interface MineCountItem {
  status: string;
  /** Read by the Follow-up due figure only (BUILD-046 G4). */
  followUpDate?: unknown;
}

/** The Overdue figure counts with THE one overdue rule (overdueRule.ts), the same function the rows, the Overdue filter and Recommended call. */
function oneRuleOverdue(today: unknown): (item: MineCountItem) => boolean {
  return (item) => isOverdueTask(item as OverdueTask, today);
}

export interface ViewCountsInput {
  view: HostView;
  /** The Caller's own loaded tasks (My Work); null while they are not loaded. */
  mine: readonly MineCountItem[] | null;
  /** The Phoenix day (YYYY-MM-DD) the Overdue figure is counted on. The page passes the one it renders with; without it only the server's flag can make a task overdue. */
  today?: string;
  /** True for a task of `mine` the page counts as overdue. Default: the one overdue rule (overdueRule.ts) at `today` - open, assignment live, shown in full, and a past date or the server's flag. */
  isOverdue?: (item: MineCountItem) => boolean;
  /** How many tasks the shared queue list holds for the Caller; null while it is not loaded. */
  sharedLoaded: number | null;
  team: {
    filter: OversightFilter;
    scope: 'manager' | 'company' | null;
    /** How many tasks are loaded; null while they are not. */
    loaded: number | null;
    /** True when every page is loaded (the open list arrives whole). */
    complete: boolean;
    /** The list is not served (an older server) or failed: the figure is Unavailable. */
    unavailable?: boolean;
  };
}

function figure(key: string, label: string, value: number | null, partial = false): CountFigure {
  return { key, label, value, text: value === null ? '…' : partial ? `${value}+` : String(value), partial };
}

function unavailableFigure(key: string, label: string): CountFigure {
  return { key, label, value: null, text: 'Unavailable', partial: false };
}

const MINE_STATUSES: readonly { key: string; label: string }[] = Object.freeze([
  { key: 'active', label: 'Active' },
  { key: 'in_progress', label: 'In progress' },
  { key: 'waiting', label: 'Waiting' },
  { key: 'blocked', label: 'Blocked' },
  { key: 'completed', label: 'Completed' },
]);

/** The Follow-up due figure counts with `followUpDueOn` (attentionModel.ts): an unfinished task whose next follow-up date is today or has passed, on the Phoenix day the page supplies. Without a day nothing is counted as due. */
function followUpDue(today: unknown): (item: MineCountItem) => boolean {
  return (item) => typeof today === 'string' && followUpDueOn(item as OverdueTask & { followUpDate?: unknown }, today);
}

function mineFigures(items: readonly MineCountItem[] | null, isOverdue: (item: MineCountItem) => boolean, isFollowUpDue: (item: MineCountItem) => boolean): CountFigure[] {
  // 2026-10-24 Owner correction 1: "All open" first - every unfinished task (Active, In progress, Waiting, Blocked), the same set the view lists.
  const out: CountFigure[] = [figure('mine-open', 'All open', items === null ? null : items.filter((i) => isOpenTask(i as { status?: unknown; lifecycle?: unknown })).length)];
  out.push(...MINE_STATUSES.map((s) => figure(`mine-${s.key}`, s.label, items === null ? null : items.filter((i) => i.status === s.key).length)));
  out.push(figure('mine-overdue', 'Overdue', items === null ? null : items.filter((i) => isOverdue(i)).length));
  out.push(figure('mine-follow-up-due', 'Follow-up due', items === null ? null : items.filter((i) => isFollowUpDue(i)).length));
  return out;
}

function scopeWords(scope: 'manager' | 'company' | null): string {
  return scope === 'manager' ? 'direct reports' : scope === 'company' ? 'company' : 'team';
}

function teamFigures(team: ViewCountsInput['team']): CountFigure[] {
  const words = scopeWords(team.scope);
  const label = team.filter === 'completed' ? `Completed tasks (${words})` : `Open tasks (${words})`;
  const key = team.filter === 'completed' ? 'team-completed' : 'team-open';
  if (team.unavailable === true) return [unavailableFigure(key, label)];
  return [figure(key, label, team.loaded, team.loaded !== null && !team.complete)];
}

function headingOf(view: HostView, team: ViewCountsInput['team']): string {
  if (view === 'my-work') return 'Your tasks';
  if (view === 'group-shared') return 'Shared queue you can take';
  return `Team Work (${scopeWords(team.scope)})`;
}

export function describeViewCounts(input: ViewCountsInput): ViewCounts {
  const overdue = input.isOverdue ?? oneRuleOverdue(input.today);
  const figures =
    input.view === 'my-work'
      ? mineFigures(input.mine, overdue, followUpDue(input.today))
      : input.view === 'group-shared'
        ? [figure('shared-queue', 'Waiting in the shared queue', input.sharedLoaded)]
        : teamFigures(input.team);
  return { view: input.view, heading: headingOf(input.view, input.team), figures };
}

// ───────────────────────── the number beside each tab (Layout-5) ─────────────────────────
//
// Rules, one per tab. A tab shows a count ONLY from data the page already holds for exactly the scope the tab is about; otherwise it shows none.
//   My Tasks        the Caller's OPEN tasks (Active + Waiting + Blocked) among the whole My tasks list the page loads for the Caller (GET /work, all statuses,
//                   not paged). It does not depend on the search, the Status choice or the Filter. The server's bell figure "openTaskCount" counts the same set
//                   (same owner rows, same projection, same source collapse), which the browser check compares. None while the list is not loaded.
//   Shared Tasks    the shared-queue figure of the labelled counts (GET /work/bell `sharedQueueCount`): the tasks the Caller can take, the figure the page already
//                   reads. None while it loads or when it cannot be read ("Unavailable" is not shown beside a tab).
//   Assigned by Me  only when the issued list is READY and fully loaded (no further page): the number of requests. Opening the tab loads the list; the page
//                   never reads it just to make a number.
//   Team / Company  only when the OPEN oversight list has already been loaded for this Caller (it arrives whole): the number of open tasks in scope.
// Every count is for the current Caller: a Caller change clears the lists and the bell, so the number goes away and returns for the new Caller.

export interface TabCount {
  value: number;
  /** The number as shown beside the tab. */
  text: string;
  /** The clearly scoped phrase the accessible name carries: "4 open", "3 you can take", "2 requests". */
  phrase: string;
}

export interface TabCounts {
  mine: TabCount | null;
  shared: TabCount | null;
  issued: TabCount | null;
  team: TabCount | null;
}

export interface TabCountsInput {
  /** The Caller's whole loaded My tasks list (every status); null while it is not loaded. */
  mine: readonly MineCountItem[] | null;
  /** The labelled counts (workCountsController.describeCounts); only the shared-queue figure is read. */
  bell: CountsView | null;
  /** The issued list: `ready` and `complete` (no further page) are both needed for a count. Null when the tab does not exist for the Caller. */
  issued: { ready: boolean; complete: boolean; count: number } | null;
  /** How many tasks the OPEN oversight list holds; null unless it is loaded for this Caller. */
  team: { loaded: number | null } | null;
}

const OPEN_STATUSES: readonly string[] = Object.freeze(['active', 'in_progress', 'waiting', 'blocked']);

function tabCount(value: number | null, phrase: (n: number) => string): TabCount | null {
  if (value === null || !Number.isInteger(value) || value < 0) return null;
  return { value, text: String(value), phrase: phrase(value) };
}

export function describeTabCounts(input: TabCountsInput): TabCounts {
  const sharedCount = input.bell?.shared.count;
  return {
    mine: tabCount(input.mine === null ? null : input.mine.filter((item) => OPEN_STATUSES.includes(item.status)).length, (n) => `${n} open`),
    shared: tabCount(sharedCount !== undefined && sharedCount.kind === 'value' ? sharedCount.value : null, (n) => `${n} you can take`),
    issued: tabCount(input.issued !== null && input.issued.ready && input.issued.complete ? input.issued.count : null, (n) => `${n} ${n === 1 ? 'request' : 'requests'}`),
    team: tabCount(input.team === null ? null : input.team.loaded, (n) => `${n} open`),
  };
}
